import { Link, useParams } from "react-router-dom";
import { Navigation, TrainFront } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Reveal, SectionHead } from "@/components/Reveal";
import { FaqAccordion } from "@/components/FaqAccordion";
import { AreaCta } from "@/components/Areas";
import { FindUs } from "@/components/home/HomeTop";
import { HoursList, MapFrame } from "@/components/VisitBlock";
import { AREAS_BY_STATE, STATES, areaPath, getArea, nearbyAreas } from "@/lib/areas";
import { BUSINESS, CATEGORIES, shopCategoryUrl } from "@/lib/site";
import NotFound from "@/pages/NotFound";

const placeName = (a) => (a.state === "washington-dc" ? `${a.name}, DC` : `${a.name}, ${STATES[a.state].short}`);

const buildFaqs = (a) => [
  { q: `Is there a dispensary near ${a.name}?`, a: `Yes. District Gaslight is an ABCA-licensed medical cannabis dispensary at ${BUSINESS.addressLine}, about ${a.mi} miles from ${a.name}. We're upstairs next to Chicken + Whiskey and open daily from 10 AM, until 11 PM on Fridays and Saturdays.` },
  { q: `How do I get to District Gaslight from ${a.name}?`, a: `${a.directions} By Metro: ${a.metro}` },
  a.state === "washington-dc"
    ? { q: "What do I need to bring?", a: "A valid 21+ government-issued photo ID and your medical cannabis registration. DC residents 21+ can self-certify online through ABCA." }
    : { q: `Can ${STATES[a.state].name} residents buy at District Gaslight?`, a: "Yes. Register through ABCA's non-DC resident form or bring a valid medical cannabis card from your home state, plus a 21+ photo ID. Cannabis purchased in DC should not be transported across state lines." },
];

export default function AreaPage() {
  const { state = "", slug = "" } = useParams();
  const area = slug.startsWith("dispensary-near-") ? getArea(state, slug.replace(/^dispensary-near-/, "")) : null;
  if (!area) return <NotFound />;
  const s = STATES[area.state];
  const path = areaPath(area);
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Areas We Serve", path: "/areas-we-serve" },
    { name: s.name, path: `/areas-we-serve/${area.state}` },
    { name: area.name, path },
  ];
  const faqs = buildFaqs(area);
  const faqLd = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };

  return (
    <>
      <Seo
        title={`Dispensary Near ${placeName(area)} | District Gaslight on 14th St NW`}
        description={`Medical cannabis dispensary near ${placeName(area)}: District Gaslight is ~${area.mi} mi away at 1738 14th St NW, DC. Directions, Metro, hours and order-ahead pickup.`}
        path={path}
        crumbs={crumbs}
        jsonLd={faqLd}
      />
      <PageHeader crumbs={crumbs} eyebrow={area.ward ? `Ward ${area.ward} · ~${area.mi} mi away` : `${s.name} · ~${area.mi} mi away`} title="Medical cannabis dispensary near" highlight={placeName(area)} image="/img/storefront.webp" lead={area.intro} />

      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-2" data-testid="area-directions">
        <Reveal>
          <SectionHead eyebrow="Directions" title={`Getting here from ${area.name}`} />
          <div className="mt-8 space-y-6">
            <div className="flex gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-lime"><Navigation className="h-4 w-4" /></span>
              <p className="pt-2 text-neutral-700" data-testid="area-directions-text">{area.directions}</p>
            </div>
            <div className="flex gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-lime"><TrainFront className="h-4 w-4" /></span>
              <p className="pt-2 text-neutral-700" data-testid="area-metro-text">{area.metro}</p>
            </div>
          </div>
          {area.state !== "washington-dc" && (
            <p className="mt-8 rounded-2xl bg-brand-sand p-5 text-sm text-neutral-600">Visiting from {s.name}? <a href={BUSINESS.nonDcCardUrl} target="_blank" rel="noopener noreferrer" className="font-bold text-brand-red">Register with ABCA's non-DC form</a> before you come.</p>
          )}
          <div className="mt-10"><HoursList /></div>
        </Reveal>
        <Reveal delay={0.1}><MapFrame className="h-[380px] lg:h-full lg:min-h-[460px]" /></Reveal>
      </section>

      <FindUs />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24">
        <SectionHead eyebrow="Shop ahead" title={`What ${area.name} patients can pick up`} />
        <ul className="mt-8 flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <li key={c.slug}><Link to={shopCategoryUrl(c.slug)} className="chip" data-testid={`area-cat-${c.slug}`}>{c.name}</Link></li>
          ))}
        </ul>
        <p className="mt-6 max-w-3xl text-sm leading-relaxed text-neutral-600" data-testid="area-card-note">
          To buy, you'll need a 21+ photo ID and a medical cannabis registration.{" "}
          {area.state === "washington-dc"
            ? <>DC residents can <Link to="/medical-card" className="font-bold text-brand-red">self-certify online in minutes</Link>.</>
            : <>{STATES[area.state].name} visitors can <Link to="/medical-card" className="font-bold text-brand-red">register with ABCA's non-DC form</Link>.</>}
          {" "}Then <Link to="/shop-all" className="font-bold text-brand-red">order ahead from the live menu</Link> or check <Link to="/special-deals" className="font-bold text-brand-red">today's deals</Link>.
        </p>
      </section>

      <section className="border-t border-neutral-200 bg-white py-20" data-testid="area-faq">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <SectionHead eyebrow="Questions" title={`${area.name} FAQ`} />
            <p className="mt-6 text-sm font-bold">Nearby areas</p>
            <ul className="mt-3 flex flex-wrap gap-2">
              {nearbyAreas(area).map((n) => (
                <li key={n.slug}><Link to={areaPath(n)} className="chip" data-testid={`nearby-${n.slug}`}>{n.name}</Link></li>
              ))}
              <li><Link to={`/areas-we-serve/${area.state}`} className="chip">All {s.short} areas</Link></li>
            </ul>
          </div>
          <div className="lg:col-span-3"><FaqAccordion items={faqs} /></div>
        </div>
      </section>
      <section className="bg-brand-sand py-16" data-testid="area-more-links">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead eyebrow="Explore more" title={area.ward ? `More Ward ${area.ward} neighborhoods` : `More ${s.name} areas`} />
          <ul className="mt-8 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {AREAS_BY_STATE[area.state].filter((b) => b.slug !== area.slug && (area.ward ? b.ward === area.ward : true)).slice(0, 12).map((b) => (
              <li key={b.slug}><Link to={areaPath(b)} data-testid={`more-area-${b.slug}`} className="block rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm font-bold transition-colors hover:border-brand-ink">Dispensary near {b.name}</Link></li>
            ))}
          </ul>
          <p className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-bold">
            <Link to="/shop-all" className="text-brand-red">Shop the live menu</Link>
            <Link to="/special-deals" className="text-brand-red">Today's deals</Link>
            <Link to="/medical-card" className="text-brand-red">Get a medical card</Link>
            <Link to="/location" className="text-brand-red">Hours &amp; directions</Link>
            {Object.values(STATES).filter((x) => x.key !== area.state).map((x) => (
              <Link key={x.key} to={`/areas-we-serve/${x.key}`} className="text-brand-red">{x.name} areas</Link>
            ))}
          </p>
        </div>
      </section>
      <AreaCta where="area-page" />
    </>
  );
}
